<template>
  <div class="preika-auth-page preika-login-page">
    <main class="preika-auth-shell">
      <section class="preika-login-showcase" :aria-label="productName + 'のご案内'">
        <div class="preika-login-showcase__grid" aria-hidden="true"></div>

        <header class="preika-login-showcase__header">
          <div class="preika-login-wordmark" :aria-label="productName">
            <span class="preika-login-wordmark__name">{{ brand }}<span
              class="preika-login-wordmark__area"
            >{{ productArea }}</span></span>
            <span class="preika-login-wordmark__line"></span>
          </div>
          <!--
            ここには「AKI INTERNAL WORKSPACE」の札が出ていた。
            **この1枚を読むのは、買った会社の社員。**自分の会社の
            システムを開いたつもりで、他社の社内システムだと言われる。

            差し替えではなく外した。ログインの前は、どの会社の人が
            見ているのか分からない。**どの会社にも当てはまる言葉しか
            置けない**ので、置けるものが無い。
          -->
        </header>

        <div class="preika-login-showcase__visual" aria-hidden="true">
          <img
            v-if="imageAvailable"
            src="/img/auth/preika_login_workspace.png"
            alt=""
            class="preika-login-showcase__image"
            @error="imageAvailable = false"
          >
        </div>

        <div class="preika-login-showcase__copy">
          <p class="preika-login-showcase__eyebrow">WORKPLACE PLATFORM</p>
          <h1>
            日々の業務を、<br>
            <span>ひとつの場所へ。</span>
          </h1>
          <!--
            **メニューにある名前で書く。**「月次の報告」という画面は無い
            （あるのは「業務報告」と、事務・管理向けの「月次集計」）。

            言い換えを重ねない。「スムーズに」「プラットフォーム」と
            並べても、何ができるかは増えない。「業務プラットフォーム」は
            言い過ぎでもある。扱うのは勤怠とそのまわりで、職場の基盤
            全体ではない。
          -->
          <p class="preika-login-showcase__description">
            勤務表と業務報告、休暇や休日出勤の申請、
            通勤費と経費の精算まで。毎日の手続きを、ここでまとめます。
          </p>
        </div>

        <footer class="preika-login-showcase__footer">
          <span>{{ productName }}</span>
          <span>© {{ currentYear }} AKI Inc.</span>
        </footer>
      </section>

      <section class="preika-login-form-panel">
        <div class="preika-login-form-wrap">
          <div class="preika-login-mobile-wordmark preika-login-wordmark" :aria-label="productName">
            <span class="preika-login-wordmark__name">{{ brand }}<span
              class="preika-login-wordmark__area"
            >{{ productArea }}</span></span>
            <span class="preika-login-wordmark__line"></span>
          </div>

          <div class="preika-login-form__meta">
            <span class="preika-login-form__secure">
              <CIcon name="cil-shield-alt"/>
              SECURE SIGN IN
            </span>
            <span class="preika-login-form__restricted">関係者専用</span>
          </div>

          <!--
            見出しと説明は、いま聞いていることに合わせる。

            確認コードを聞いているのに「メールアドレスとパスワードを
            入力してください」と出したままにすると、利用者は自分が
            どこにいるのか分からなくなる。
          -->
          <!--
            **2要素の登録の段でも言い分ける**（束22、2026-09-30）。以前は
            登録パネルを出しているあいだも「PREIKA 勤怠にログイン／メール
            アドレスとパスワードを入力してください」のままで、いまが登録の段
            だと読み取れなかった。
          -->
          <p class="preika-login-form__eyebrow">
            {{ mfaRequired || enrollmentRequired ? 'ONE MORE STEP' : 'WELCOME BACK' }}
          </p>
          <h2>{{ headingText }}</h2>
          <p class="preika-login-form__description">
            <template v-if="mfaRequired">
              あと1つ、確認コードの入力が必要です。
            </template>
            <!--
              ［控えました。次へ］を断られたあとは出さない。登録はもう済んで
              いて、この画面からはログインが完了しない（事実でなくなる）。
            -->
            <template v-else-if="enrollmentRequired && completionRefused" />
            <template v-else-if="enrollmentRequired">
              認証アプリを登録するとログインが完了します。
            </template>
            <template v-else>
              登録されているメールアドレスとパスワードを入力してください。
            </template>
          </p>
          <a
            v-if="!mfaRequired && !enrollmentRequired && productSiteUrl"
            class="preika-login-product-link"
            :href="productSiteUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>サービス紹介・機能・料金を見る</span>
            <CIcon name="cil-arrow-right"/>
          </a>

          <!--
            Passkeyのボタンは、端末が対応していて、かつPREIKAが入口を
            開けているときだけ出す（サーバへ聞く）。どちらか一方でも
            欠けていれば、押しても必ず失敗するボタンになる。

            画面側の設定だけで決めると、画面は開いているのにサーバは
            閉じている、というずれが起きる。

            **メールアドレスを聞かない。** 誰かは鍵からサーバが決める。
            先にメールアドレスを聞く形にすると、入れた時点で「その人が
            Passkeyを持っているか」が画面の反応から分かってしまう。
          -->
          <!--
            確認コードを聞いている最中は、パスキーもメールアドレスも出さない。

            1つ目は通っているので、ここで別の入口を見せる意味がない。
            見せると、途中でやり直したのか続きなのかが分からなくなる。
          -->
          <!--
            失敗の文言は、**どの段にいても見える場所**に置く。

            2026-08-22 まで、ここは下の ValidationObserver の中にあった。
            あれは v-if="!mfaRequired && !enrollmentRequired" なので、
            **2要素のパネルが出ているあいだ、表示先ごと消えていた。**

            文言は this.errors に入っているのに画面は無反応。共有鍵が
            取れなかったとき・6桁を打ち間違えたとき・「控えました」が
            失敗したとき・期限が切れたとき、利用者は何も知らされないまま
            同じ画面を見続けることになっていた。

            単体テストは wrapper.vm.errors.length を見ていたので、
            **表示先が消えていても緑のまま通り抜けた。**
          -->
          <div
            v-if="errors.length"
            ref="errorBox"
            class="alert alert-danger"
            role="alert"
            aria-live="assertive"
          >
            <strong>ログインできませんでした</strong>
            <div
              v-for="error in errors"
              :key="error.error_id || error.error_name || error"
            >
              {{ error.error_name || error }}
            </div>
          </div>

          <!--
            ［控えました。次へ］を断られたあと、断りの文のすぐ下に出す1文。

            断りの文（「ログイン情報を確認できません。」など）だけだと、
            その下に「登録できました。予備のコードを控えてください」が並び、
            登録は済んでいて入り直せばよいことが読み取れなかった。
            文は data の completionRefusedNote の1か所だけに置いてある。
          -->
          <p
            v-if="enrollmentRequired && completionRefused"
            class="preika-login-enroll__note preika-login-enroll__refused"
          >
            {{ completionRefusedNote }}
          </p>

          <!--
            2要素の登録がまだで、先へ進めない状態。

            **確認コード待ちとは別の段。**あちらは「持っているものを
            見せて」で、こちらは「まず持ってください」。混ぜると、まだ
            登録していない人へ「確認コードを入れてください」と出す。
          -->
          <div v-if="enrollmentRequired" class="preika-login-enroll">
            <!-- 予備のコードが出るまでは、登録の手順。 -->
            <template v-if="!backupCodes.length">
              <!--
                **なぜ登録するのかを、段ごとに言い分ける**（人の目の洗い出し
                束22）。

                2026-09-30 まで、段に関係なく「会社の方針により、管理者は
                2要素認証の登録が必須となっています」と出し、そのすぐ下に
                ［あとで登録する］があった。いつから必須か・あと何回押せるかは
                受け取っているのに出していなかった。一般社員が一時パスで
                復旧したときにも同じ文が出た（その人は管理者でも必須でもない）。

                どの段かはサーバが決める（押せるか・復旧か）。画面は受け取った
                ものを言葉にするだけ。
              -->
              <p
                v-if="recoveryEnrollment"
                class="preika-login-enroll__note preika-login-enroll__stage preika-login-enroll__stage--recovery"
              >
                一時パスを入力したため、以前の2要素認証の登録と予備のコードは無効になりました。ログインするには、新しく認証アプリを登録してください。
              </p>
              <p
                v-else-if="enrollmentSkippable"
                class="preika-login-enroll__note preika-login-enroll__stage preika-login-enroll__stage--prompted"
              >
                {{ promptedStageText }}
              </p>
              <p
                v-else
                class="preika-login-enroll__note preika-login-enroll__stage preika-login-enroll__stage--enforced"
              >
                会社の方針により、管理者は2要素認証の登録が必須となっています。登録を完了してください。
              </p>

              <!--
                **QR を出す。鍵は畳む**（束22）。

                以前はここに32文字の鍵だけが大きく出て、スマホの認証アプリで
                読み取れず、手で打つしかなかった。セキュリティ設定と同じく
                端末の中で QR を描き、鍵は「読み取れないとき」に畳む——開いた
                まま鍵を大きく出すと、その下の「6桁」を聞かれた人が
                **鍵のほうを入れるものだと読む。**
              -->
              <p class="preika-login-enroll__step">
                <b>1.</b> 認証アプリ（Google Authenticator、Microsoft Authenticator
                など）で、このQRコードを読み取ります。
              </p>
              <img
                v-if="enrollmentQr"
                class="preika-login-enroll__qr"
                :src="enrollmentQr"
                alt="認証アプリ用QRコード"
              >
              <!--
                共有鍵は画面の中だけで持つ。**記録にも貼らない。**
                1行残ると、そこからその人のコードを作れる。
              -->
              <details v-if="enrollmentSecret" class="preika-login-enroll__manual">
                <summary>QRコードを読み取れないとき</summary>
                <p class="preika-login-enroll__manual-note">
                  認証アプリの「手動で入力」から、次のキーを登録してください。
                </p>
                <p class="preika-login-enroll__secret">
                  {{ enrollmentSecret }}
                </p>
              </details>
              <p class="preika-login-enroll__step">
                <b>2.</b> 登録後、<b>アプリの画面に出ている6桁の数字</b>を入れてください。（上のキーではありません）
              </p>
              <form
                class="preika-login-form"
                novalidate
                @submit.prevent="submitEnrollment"
              >
                <CInput
                  v-model="mfaCode"
                  class="preika-login-mfa__input"
                  autocomplete="one-time-code"
                  inputmode="numeric"
                  placeholder="6桁の数字"
                  :disabled="mfaLoading"
                />
                <button
                  type="submit"
                  class="btn btn-primary preika-login-mfa__button"
                  :disabled="mfaLoading"
                >
                  <span
                    v-if="mfaLoading"
                    class="my-spinner"
                    aria-hidden="true"
                  ></span>
                  <span>{{ mfaLoading ? '確認しています' : '登録する' }}</span>
                </button>
              </form>
            </template>

            <!--
              予備のコードは、この1回しか見せられない。
              **控える前に先へ進ませない。**進ませると、端末を失くした
              ときの逃げ道が無いまま運用が始まる。
            -->
            <template v-else>
              <p class="preika-login-enroll__note">
                登録できました。予備のコードを控えてください。
                <b>この画面を閉じると二度と見られません。</b>
              </p>
              <ul class="preika-login-enroll__codes">
                <li v-for="code in backupCodes" :key="code">{{ code }}</li>
              </ul>
              <!--
                断られたあとは出さない。控え待ちはもう使い切られていて、
                もう一度押しても通らない（二度と通らないボタンを出したままに
                しない）。予備のコードは出したままにして、下の
                「最初からやり直す」でログインへ戻れるようにする。
              -->
              <button
                v-if="!completionRefused"
                type="button"
                class="btn btn-primary preika-login-mfa__button"
                :disabled="mfaLoading"
                @click="finishEnrollment"
              >{{ mfaLoading ? '進んでいます' : '控えました。次へ' }}</button>
            </template>

            <!--
              「あとで」。**促しの段でだけ出す。**

              まだ必須ではない期間に、逃げ道の無い画面を出さない。
              出すと、登録の準備ができていない人がその場で詰まる。
              認証アプリを入れていない人は、たいていその場では入れられない。

              押せるかどうかを決めるのはサーバ。**画面の状態では決めない。**
              ここを画面任せにすると、必須になった当日に、開きっぱなしの
              タブから通り抜けられる。
            -->
            <button
              v-if="enrollmentSkippable && !backupCodes.length
                && !recoveryEnrollment"
              type="button"
              class="btn btn-link preika-login-enroll__later"
              :disabled="mfaLoading"
              @click="skipEnrollment"
            >あとで登録する</button>
            <!--
              **行き止まりにしない。**

              2026-08-23 まで、このパネルには入り直す道が1つも無かった。
              暗号鍵が設定されていない環境では共有鍵が出ないので、
              6桁を入れることもできない。メール／パスワードの欄も
              v-if で隠れている。F5 しても、入り直すとまた登録待ちへ
              送られる（場面 S-62 / S-70）。

              予備のコードを出したあとは押させない。あれは**この1回しか
              見せられない**ので、控える前に画面を捨てさせない。

              **ただし、［控えました。次へ］を押して断られたあとは出す**
              （2026-10-09）。控え待ちはサーバの側でもう使い切られていて、
              もう一度押しても通らない。ここを隠したままだと、断りの文だけが
              出て、ログインへ戻る道が再読込しか無かった（待ちの期限が切れた
              とき・待っているあいだに2要素が変わったとき）。本人は
              ［控えました］を押しているので、控える前に捨てさせることには
              ならない。
            -->
            <button
              v-if="!backupCodes.length || completionRefused"
              type="button"
              class="btn btn-link preika-login-enroll__back"
              :disabled="mfaLoading"
              @click="cancelMfa"
            >最初からやり直す</button>
          </div>

            <!--
              **一時パスの入力（場面 S-48）。**

              確認コードの欄と<b>入れ替える</b>。並べて出すと、どちらへ
              入れるのか分からなくなる。
            -->
            <div
              v-if="temporaryPassInputShown"
              class="preika-login-recovery"
            >
              <!--
                **一時パスを出せるのは PREIKA 運営だけ**（束22）。以前は
                「社内の管理者から聞いた」と書いていたが、会社の管理者の
                画面には一時パスを出す操作が無い（わざと置いていない）。
              -->
              <p class="preika-login-recovery__note">
                PREIKAサポート窓口から聞いた一時パスを入力してください。
                <br />
                入れたあと、認証アプリの登録をやり直します。
              </p>
              <!--
                一時パスをまだ持っていない人の次の一手。ヘルプ（管理者の
                2要素認証と復旧）と同じ順：社内の管理者に外してもらう、
                それができないときに運営へ。
              -->
              <p class="preika-login-recovery__note preika-login-recovery__next">
                社内の管理者に2要素認証を解除してもらうか、PREIKAサポート窓口へ連絡してください。
              </p>
              <form
                class="preika-login-form"
                novalidate
                @submit.prevent="submitTemporaryPass"
              >
                <CInput
                  v-model="temporaryPass"
                  class="preika-login-recovery__input"
                  autocomplete="off"
                  inputmode="text"
                  placeholder="一時パス"
                  :disabled="mfaLoading"
                />
                <button
                  type="submit"
                  class="btn btn-primary preika-login-recovery__button"
                  :disabled="mfaLoading"
                >
                  <span
                    v-if="mfaLoading"
                    class="my-spinner"
                    aria-hidden="true"
                  ></span>
                  <span>{{ mfaLoading ? "確認しています" : "確認する" }}</span>
                </button>
              </form>
              <!--
                **戻る道を残す。**押し間違えた人が確認コードの欄へ
                戻れないと、そこが行き止まりになる。
              -->
              <button
                type="button"
                class="btn btn-link preika-login-recovery__back"
                :disabled="mfaLoading"
                @click="hideTemporaryPassInput"
              >
                確認コードの入力へ戻る
              </button>
            </div>

          <div
              v-if="mfaRequired && !temporaryPassInputShown"
              class="preika-login-mfa"
            >
            <!-- 見出し側で「あと1つ」と言っているので、ここでは言い直さない。 -->
            <p class="preika-login-mfa__note">
              認証アプリの6桁の数字、または予備のコードが使えます。
            </p>
            <form class="preika-login-form" novalidate @submit.prevent="submitMfa">
              <CInput
                v-model="mfaCode"
                class="preika-login-mfa__input"
                autocomplete="one-time-code"
                inputmode="text"
                placeholder="確認コード"
                :disabled="mfaLoading"
              />
              <button
                type="submit"
                class="btn btn-primary preika-login-mfa__button"
                :disabled="mfaLoading"
              >
                <span v-if="mfaLoading" class="my-spinner" aria-hidden="true"></span>
                <span>{{ mfaLoading ? '確認しています' : '確認する' }}</span>
              </button>
            </form>
            <!--
              **どちらも使えない人の道（場面 S-48）。**

              認証アプリの端末も予備のコードも失った人は、ここまで来て
              詰まる。「最初からやり直す」を押しても、合言葉からやり直して
              同じところへ戻るだけ。

              出すかどうかは**サーバが決める**。閉じているときに出すと、
              押しても必ず断られるボタンになる。
            -->
            <button
              v-if="temporaryPassEntranceOpen"
              type="button"
              class="btn btn-link preika-login-mfa__recovery"
              :disabled="mfaLoading"
              @click="showTemporaryPassInput"
            >
              認証アプリも予備のコードも使えない
            </button>
            <!--
              **入口が閉じているときも、次の一手を1行だけ置く**（見直しの指摘、
              2026-09-30）。本番の既定は閉。以前は上のボタンごと消えて、
              誰に何を頼めばよいかが画面に1行も無かった。一時パスの話は
              しない（閉じているので使えない）。
            -->
            <p
              v-else
              class="preika-login-mfa__note preika-login-mfa__lost"
            >
              社内の管理者に2要素認証を解除してもらうか、PREIKAサポート窓口へ連絡してください。
            </p>
            <!--
              入り直す道を残す。端末を持っていない・予備も手元に無い
              ときに、行き止まりにしない。
            -->
            <button
              type="button"
              class="btn btn-link preika-login-mfa__back"
              :disabled="mfaLoading"
              @click="cancelMfa"
            >
              最初からやり直す
            </button>
          </div>

          <div
            v-if="!mfaRequired && !enrollmentRequired && passkeySupported"
            class="preika-login-passkey"
          >
            <button
              type="button"
              class="btn btn-outline-primary preika-login-passkey__button"
              :disabled="loading || passkeyLoading"
              @click="loginWithPasskey"
            >
              <span v-if="passkeyLoading" class="my-spinner" aria-hidden="true"></span>
              <span>{{ passkeyLoading ? '確認しています' : 'パスキーでログイン' }}</span>
            </button>
            <p class="preika-login-passkey__note">
              この端末に登録済みなら、顔・指紋・PINで入れます。
            </p>
            <p
              v-if="passkeyHint"
              class="preika-login-passkey__hint"
            >{{ passkeyHint }}</p>
          </div>

          <!--
            外部IdPのボタンも、PREIKAが入口を開けているときだけ出す
            （サーバへ聞く）。パスキーと同じ理由で、画面側の設定だけで
            決めると「押せるのに通らない」ずれが起きる。

            **メールアドレスを聞かない。** 誰かは戻ってきたトークンから
            サーバが決める。先に聞く形にすると、入れた時点で「その人が
            その提供元を結び付けているか」が画面の反応から分かってしまう。

            行き先のURLもサーバが組み立てる。画面から渡せる形にすると、
            戻り先を差し替えて認可コードを外へ飛ばせる。

            **提供元ごとに書き写さない。** 写すと、片方だけ直して片方が
            残る。サーバ側で、提供元を1つしか見ていない繰り返しが実際に
            見つかっている。増やすときは EXTERNAL_IDPS へ1行足す。
          -->
          <div
            v-for="idp in availableExternalIdps"
            :key="idp.key"
            :class="['preika-login-idp', 'preika-login-' + idp.key]"
          >
            <button
              type="button"
              :class="['btn', 'preika-login-idp__button',
                       idp.key === 'google'
                         ? 'preika-login-google__button'
                         : 'btn-outline-primary']"
              :disabled="loading || passkeyLoading || isAnyExternalIdpMoving"
              @click="loginWithExternalIdp(idp)"
            >
              <span
                v-if="externalIdpState[idp.key].loading"
                class="my-spinner"
                aria-hidden="true"
              ></span>
              <!--
                **Google のロゴは、決まった形でしか出せない。**
                標準の4色。単色にしない。色を変えない。
                （https://developers.google.com/identity/branding-guidelines）

                この4色は Google のブランドの色そのものなので、
                この製品の配色へ寄せてはいけない。
              -->
              <svg
                v-else-if="idp.key === 'google'"
                class="preika-login-google__logo"
                width="18"
                height="18"
                viewBox="0 0 48 48"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                />
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                />
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                />
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                />
              </svg>
              <span>{{ externalIdpState[idp.key].loading ? '移動しています' : idp.label }}</span>
            </button>
            <p class="preika-login-idp__note">{{ idp.note }}</p>
          </div>

          <!--
            区切り線は、上に何か1つでも出ているときだけ。
            どちらも出ていないのに「または」だけが残ると、何と何の
            「または」なのか分からない。
          -->
          <div
            v-if="!mfaRequired && !enrollmentRequired
              && (passkeySupported || availableExternalIdps.length > 0)"
            class="preika-login-divider"
          ><span>または</span></div>

          <!-- 送信時に ref 経由で validate() を呼ぶ。slotの状態は使わない。 -->
          <ValidationObserver v-if="!mfaRequired && !enrollmentRequired" ref="obs">
            <CForm
              class="preika-login-form"
              novalidate
              @submit.prevent="loginData"
            >

              <!--
                ログインは「すでにある資格情報を入れる場所」なので、
                打っている途中では何も言わない。押されてから確かめる。
                passive は validate() を呼んだときだけ検証する指定。

                書式の判定もしない。ここで弾いても正しいかは分からず、
                当社の正規表現が許さない形のアドレス（+付きなど）を
                使っている人が、サーバへ届く前に締め出される。
                合っているかどうかはサーバが答える。
              -->
              <ValidationProvider
                name="メールアドレス"
                rules="required|max:254"
                v-slot="providerProps"
                mode="passive"
              >
                <div class="preika-login-field">
                  <label for="preika-login-email">メールアドレス</label>
                  <CInput
                    id="preika-login-email"
                    placeholder="name@example.com"
                    autocomplete="username email"
                    autofocus
                    v-model="email"
                    :is-valid="providerProps.errors.length ? false : null"
                    :aria-invalid="providerProps.errors.length ? 'true' : 'false'"
                  >
                    <template #prepend-content>
                      <CIcon name="cil-user"/>
                    </template>
                  </CInput>
                  <p v-if="providerProps.errors[0]" class="error">
                    {{ providerProps.errors[0] }}
                  </p>
                </div>
              </ValidationProvider>

              <!--
                複雑さの規則もここでは見ない。今のパスワードが現在の規則を
                満たしていない利用者は、正しく入力しているのに送信すらできず、
                何が悪いのかも分からないまま締め出される。
                規則を課すのは登録・変更のときだけでよい。
              -->
              <ValidationProvider
                name="パスワード"
                rules="required"
                v-slot="providerProps"
                mode="passive"
              >
                <div class="preika-login-field">
                  <div class="preika-login-field__label-row">
                    <label for="preika-login-password">パスワード</label>
                    <router-link
                      :to="{ name: 'SendEmail' }"
                      class="preika-login-form__forgot"
                    >
                      パスワードを忘れた方
                    </router-link>
                  </div>

                  <CInput
                    id="preika-login-password"
                    placeholder="パスワードを入力"
                    :type="passwordInputType"
                    autocomplete="current-password"
                    v-model="password"
                    :is-valid="providerProps.errors.length ? false : null"
                    :aria-invalid="providerProps.errors.length ? 'true' : 'false'"
                  >
                    <template #prepend-content>
                      <CIcon name="cil-lock-locked"/>
                    </template>
                    <template #append-content>
                      <button
                        type="button"
                        class="preika-login-password-toggle"
                        :aria-pressed="showPassword ? 'true' : 'false'"
                        :aria-label="showPassword ? 'パスワードを非表示にする' : 'パスワードを表示する'"
                        @click="showPassword = !showPassword"
                      >
                        {{ showPassword ? '非表示' : '表示' }}
                      </button>
                    </template>
                  </CInput>

                  <p v-if="providerProps.errors[0]" class="error">
                    {{ providerProps.errors[0] }}
                  </p>
                </div>
              </ValidationProvider>

              <!--
                無効にするのは送信中だけ。入力の正しさは押されてから確かめる。

                以前は observerProps.validated も条件に入れていたが、これは
                「検証を実行したか」であって「入力が正しいか」ではない。
                パスワードマネージャやブラウザの自動入力は input イベントを
                出さないため検証が走らず、両方が埋まって見えるのにボタンが
                ずっと押せない状態になっていた。手入力でも検証が非同期な分、
                打ち終わってすぐ押すと数百ミリ秒だけ効かない。
              -->
              <button
                type="submit"
                class="btn btn-primary preika-login-submit"
                :disabled="loading"
                :aria-busy="loading ? 'true' : 'false'"
              >
                <span v-if="loading" class="my-spinner" aria-hidden="true"></span>
                <span>{{ loading ? 'ログインしています' : productName + 'にログイン' }}</span>
              </button>
            </CForm>
          </ValidationObserver>

          <div class="preika-login-form__note">
            <span class="preika-login-form__note-icon">
              <CIcon name="cil-shield-alt"/>
            </span>
            <div>
              <strong>安全な接続で保護されています</strong>
              <p>共有端末では、利用後に必ずログアウトしてください。</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import axios from 'axios'
import ConstList from './../../ConstList'
import { BRAND, PRODUCT_AREA, PRODUCT_NAME } from '../../common/productName'
import {
  clearAccessContext,
  getAccessContext,
  setAccessContext
} from '../../services/accessContext'
import {
  isPasskeyLoginAvailable,
  isPasskeySupported,
  signInWithPasskey
} from '../../services/passkey'
import {
  isMicrosoftLoginAvailable,
  startMicrosoftLogin
} from '../../services/microsoftLogin'
import {
  isGoogleLoginAvailable,
  startGoogleLogin
} from '../../services/googleLogin'
import {
  isNetworkFailure,
  isChallengeGone,
  serverMessage,
  submitMfaCode,
  useTemporaryRecoveryPass,
  fetchPendingEnrollmentState,
  fetchMfaChallengeState,
  startPendingEnrollment,
  confirmPendingEnrollment,
  completePendingEnrollment,
  skipPendingEnrollment
} from '../../services/mfa'
import { otpauthQrDataUrl } from '../../utils/otpQr'

/**
 * ［控えました。次へ］を断られたあとに出す1文。
 *
 * Gemini が決めた文（2026-10-09、LM-R1。原文のまま）。伝える事実は3つ：
 * 認証アプリの登録は済んでいる／ログインはまだ完了していない／
 * 「最初からやり直す」から、パスワードと、認証アプリの6桁か予備のコードで入り直す。
 */
const COMPLETION_REFUSED_NOTE =
  '認証アプリの登録は完了していますが、ログインは完了していません。' +
  '［最初からやり直す］を押し、パスワードと、登録した認証アプリの6桁のコード（または予備のコード）を入力して再度ログインしてください。'

/**
 * 必須になる日を「2026年10月15日」の形にする。
 *
 * サーバは日付を "2026-10-15" の文字で返すことも、時刻の数（ミリ秒）で
 * 返すこともある（Jackson の設定しだい）。どちらでも日だけを読む。
 * 読めなければ空（「近く必須になります」とだけ言う）。**日を作らない。**
 */
function japaneseDay (value) {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'number') {
    const day = new Date(value)
    if (isNaN(day.getTime())) return ''
    return day.getFullYear() + '年' + (day.getMonth() + 1) + '月' + day.getDate() + '日'
  }
  const matched = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value))
  if (!matched) return ''
  return Number(matched[1]) + '年' + Number(matched[2]) + '月' + Number(matched[3]) + '日'
}

/**
 * ログイン画面に出す外部IdP。**足す場所はここ1つ。**
 *
 * data の外に置くのは、関数を持たせるため。data へ入れると Vue が中身を
 * 見張ろうとするし、提供元が増えるたびに状態の初期値を書き足すことになる。
 * 状態（出すか・移動中か）は key から作る。
 *
 * failureMark は、戻ってきたURLに付く印の名前。サーバが付ける
 * ?microsoft=failed / ?google=failed と対になっている。
 */
const EXTERNAL_IDPS = [
  {
    key: 'microsoft',
    label: 'Microsoft アカウントでログイン',
    note: '「セキュリティ設定」で連携済みの方だけ入れます。'
      + '（先に登録が要ります）',
    /*
     * **入口を名指ししない**（束23）。パスキーの失敗文と同じ形にそろえる。
     * パスワードでのログインを閉じた会社では、名指しした入口が使えない。
     */
    failure: 'Microsoftアカウントでログインできませんでした。' +
      'この画面に出ているほかのログイン方法をお試しください。' +
      'ログイン後に、その場で連携できます。',
    isAvailable: isMicrosoftLoginAvailable,
    start: startMicrosoftLogin
  },
  {
    key: 'google',
    /*
     * **この言い方は決まっている。**「Sign in with Google」
     * 「Sign up with Google」「Continue with Google」のどれか。
     * 日本語にするのは認められている。
     */
    label: 'Google でログイン',
    note: '「セキュリティ設定」で連携済みの方だけ入れます。'
      + '（先に登録が要ります）',
    failure: 'Googleアカウントでログインできませんでした。' +
      'この画面に出ているほかのログイン方法をお試しください。' +
      'ログイン後に、その場で連携できます。',
    isAvailable: isGoogleLoginAvailable,
    start: startGoogleLogin
  }
]

/**
 * ログインが通らなかったときの文言（場面 X-15）。
 *
 * **1種類しか無い。**メールアドレスが無い・合言葉が違う・この会社では
 * パスワードを使えない、の3つを見分けられるようにすると、この口が
 * 「その合言葉は正しい」と教える判定器になる。
 *
 * そのうえで、**入口が1つも無い人が読み進められる**ようにする。
 * パスキーに対応していない端末しか持たない社員は、会社がパスワードを
 * 閉じると開いている入口が1つも無い。
 *
 * **入口は名指ししない。**その会社でパスキーも閉じていれば、閉じている
 * 入口を案内することになる（S-64 と同じ理由）。
 */
const LOGIN_FAILED_MESSAGE =
  'メールアドレスまたはパスワードが違うか、'
  + 'この会社ではパスワードでのログインを利用できない場合があります。'
  + 'この画面に出ているほかのログイン方法もお試しください。'
  + '利用できる方法がない場合は、社内の管理者へご連絡ください。'

export default {
  name: 'Login',

  data () {
    return {
      email: '',
      password: '',
      errors: [],
      loading: false,
      passkeyLoading: false,
      passkeySupported: false,
      /*
       * **入れなかったあと、どの入口を試したか。**
       *
       * 入ったあとに設定へ連れて行くために覚えておく。ログインを
       * またぐので、画面の変数では消える。sessionStorage へ置く。
       *
       * 'passkey' / 'google' / 'microsoft' のどれか。
       */
      triedButNotRegistered: '',

      /*
       * 提供元ごとの「出すか」「移動中か」。
       *
       * 一覧から作るので、提供元を足しても**ここは触らない**。
       * 触る場所が増えるほど、片方だけ直したまま残る。
       */
      externalIdpState: EXTERNAL_IDPS.reduce((state, idp) => {
        state[idp.key] = { available: false, loading: false }
        return state
      }, {}),

      showPassword: false,
      imageAvailable: true,

      // 1つ目は通ったが、まだ入っていない状態。
      mfaRequired: false,
      mfaCode: '',
      mfaLoading: false,

      /*
       * 2要素の登録がまだで、先へ進めない状態。
       *
       * **確認コード待ちとは別に持つ。**1つの真偽値にまとめると、
       * 画面がどちらを出すか決められない。
       */
      /*
       * パスキーの入り口で伝えることがあるとき。
       *
       * **赤い枠（errors）とは別に持つ。**やめた人にとっては失敗では
       * ないので、失敗と同じ見た目にしない。
       */
      passkeyHint: '',
      enrollmentRequired: false,

      /**
       * 一時パスの入口が開いているか（場面 S-48）。
       *
       * **サーバが決める。**確認コード待ちの応答に載ってくる。
       * ここで勝手に true にしない——閉じているのに出すと、押しても
       * 必ず断られるボタンになる。
       */
      temporaryPassEntranceOpen: false,

      /** 一時パスの入力を出しているか。確認コードの欄と入れ替える。 */
      temporaryPassInputShown: false,

      /** 入力中の一時パス。**送ったら必ず消す。** */
      temporaryPass: '',

      /**
       * いまの登録が、一時パスからの復旧か（場面 S-48）。
       *
       * **「あとで」を出すかどうかだけに使う。**復旧の人は一時パスを
       * もう使い切っていて、古い2要素も消えている。あとまわしにすると
       * 2要素を1つも持たないまま入ることになる。
       *
       * 押されたときに断るのはサーバ。ここは表示のためだけ。
       */
      recoveryEnrollment: false,
      /* 「あとで」を押せるか。**サーバが決める。**画面では決めない。 */
      enrollmentSkippable: false,
      /* いつから必須か。決まっていなければ空。**日を作らない。** */
      mfaRequiredFrom: '',
      /*
       * あと何回［あとで］を押せるか（束22）。サーバが数える。回数を
       * 決めていない会社は null（回数では止めない）。表示のためだけ。
       */
      enrollmentSkipsRemaining: null,
      enrollmentSecret: '',
      enrollmentUri: '',
      /* 端末の中で描いた QR（data: の画像）。外のサービスへ出さない。 */
      enrollmentQr: '',
      backupCodes: [],
      /*
       * ［控えました。次へ］を押して、サーバに断られたか。
       *
       * 断られたら、控え待ちはもう使い切られている（やり直しても通らない）。
       * そのときだけ、予備のコードを出したまま「最初からやり直す」を出す。
       */
      completionRefused: false,
      /* 断られたあとに、断りの文のすぐ下へ出す1文（文はここだけ）。 */
      completionRefusedNote: COMPLETION_REFUSED_NOTE
    }
  },

  computed: {
    /** 売るときの正式な表記。**1か所から取る。** */
    productName () {
      return PRODUCT_NAME
    },

    /** 銘の中で、細く小さく置くほう。 */
    /** 銘の太いほう。シリーズの名前。 */
    brand () {
      return BRAND
    },

    productArea () {
      return PRODUCT_AREA
    },

    currentYear () {
      return new Date().getFullYear()
    },

    /** 一覧をそのまま画面から読めるように。 */
    externalIdps () {
      return EXTERNAL_IDPS
    },

    /**
     * いま画面に出す提供元だけ。
     *
     * <b>確認コードを聞いている最中は1つも出さない。</b>1つ目は通って
     * いるので、ここでボタンを出すと、コードを入れずにもう一度送り出せる。
     * 実際にこの条件を落とし、検査で捕まえた。
     */
    availableExternalIdps () {
      if (this.mfaRequired || this.enrollmentRequired) return []
      return EXTERNAL_IDPS.filter(idp => this.externalIdpState[idp.key].available)
    },

    /**
     * どれか1つでも移動中か。
     *
     * <b>移動中は全部押せなくする。</b>片方だけ止めると、Microsoft へ
     * 送り出した直後に Google も押せて、送り出しの記録が2本できる。
     * 戻ってきたときにどちらの続きなのか決まらない。
     */
    isAnyExternalIdpMoving () {
      return EXTERNAL_IDPS.some(idp => this.externalIdpState[idp.key].loading)
    },

    passwordInputType () {
      return this.showPassword ? 'text' : 'password'
    },

    /** 見出し。いま聞いていること（確認コード・登録・ログイン）に合わせる。 */
    headingText () {
      if (this.mfaRequired) return '本人確認'
      if (this.enrollmentRequired) return '2要素認証の登録'
      return this.productName + 'にログイン'
    },

    /**
     * 促しの段（［あとで］を押せる）の文（束22）。
     *
     * 必須になる日と、［あとで登録する］をあと何回押せるかを伝える
     * （文は Gemini が決めた。2026-10-08、LM-4）。日も回数も、サーバが返したものだけを使う。
     */
    promptedStageText () {
      const day = japaneseDay(this.mfaRequiredFrom)
      const when = day
        ? '会社の方針により、' + day + 'から管理者は2要素認証の登録が必須となります。'
        : '会社の方針により、管理者は2要素認証の登録が必須となります。'
      const remaining = typeof this.enrollmentSkipsRemaining === 'number'
        ? '（あと' + this.enrollmentSkipsRemaining + '回）'
        : ''
      return when + '［あとで登録する］を押すと今回は登録せずに進めます。' + remaining
    },

    productSiteUrl () {
      return String(process.env.VUE_APP_PRODUCT_SITE_URL || '').trim()
    },

    safeRedirect () {
      const value = String((this.$route.query && this.$route.query.redirect) || '')
      return /^\/account-invitation\?token=[A-Za-z0-9_-]{32,200}$/.test(value)
        ? value
        : '/dashboard'
    }
  },

  watch: {
    /*
     * 赤枠を出したら、そこまで画面を動かす。
     *
     * 赤枠はフォームの上にあり、間にパスキーと外部IDのボタンがあるので、
     * 小さい画面では［ログイン］を押した場所から見えなかった。
     */
    errors (value) {
      if (!value || !value.length) return
      this.$nextTick(() => {
        const box = this.$refs.errorBox
        if (box && typeof box.scrollIntoView === 'function') {
          box.scrollIntoView({ block: 'nearest' })
        }
      })
    }
  },

  mounted () {
    /*
     * **予備のコードを出しているあいだの再読込を、いったん止める。**
     *
     * この画面の状態は data() にしか無い。F5 すると全部消える。
     * 確認コード待ちと登録待ちは入り直せば同じ段へ戻れる（サーバ側の
     * 待ちは期限まで残る）が、**予備のコードだけは取り返せない。**
     * ログインの途中で配る系統には、取り直す口が無い（場面 S-69）。
     *
     * 「この画面を閉じると二度と見られません」とは書いてあるが、
     * F5 は読む前に押せてしまう。ブラウザの確認を1枚はさむ。
     */
    window.addEventListener('beforeunload', this.warnWhileBackupCodesShow)

    // 端末が対応していて、かつPREIKAが入口を開けているときだけ出す。
    // どちらか一方でも欠けていれば、押しても必ず失敗するボタンになる。
    /*
     * 外部IdPはブラウザの対応を問わない（ただの画面遷移）ので、
     * パスキーの対応判定より前に聞く。ここを下に置くと、パスキー非対応の
     * 端末で return してしまい、**外部IdPのボタンが永久に出ない**。
     */
    EXTERNAL_IDPS.forEach(idp => {
      idp.isAvailable().then(available => {
        this.externalIdpState[idp.key].available = available
      })
    })

    this.applyExternalIdpReturn()

    /*
     * **再読込のあとも、元の場所へ戻す（場面 S-48）。**
     *
     * 復旧の途中で F5 されると、一時パスは使い切り済み・古い2要素も
     * 消えている・これまでのセッションも切れている。入り直しても
     * 何も出せないので、**ここで聞かないと締め出しになる。**
     */
    this.restorePendingEnrollment()

    if (!isPasskeySupported()) return
    isPasskeyLoginAvailable().then(available => {
      this.passkeySupported = available
    })
  },

  beforeDestroy () {
    /*
     * **外し忘れない。**残すと、この画面を離れたあとも別の画面の
     * 再読込を止めにかかる。
     */
    window.removeEventListener('beforeunload', this.warnWhileBackupCodesShow)
  },

  methods: {
    /**
     * 予備のコードを出しているあいだだけ、再読込を確認させる。
     *
     * <p>止めるのはそこだけ。ふだんのログイン画面で確認が出ると、
     * <b>邪魔なだけで誰も読まなくなる。</b>読まれない確認は、
     * 本当に止めたい場面でも読まれない。</p>
     *
     * <p>文言はブラウザが決める（今のブラウザは指定した文字を出さない）。
     * こちらは「止める」ことだけを伝える。</p>
     */
    warnWhileBackupCodesShow (event) {
      if (!this.backupCodes.length) return
      event.preventDefault()
      // 古いブラウザはこちらを見る。中身は今のブラウザでは出ない。
      event.returnValue = ''
      return ''
    },

    /**
     * ログインできたあとの共通処理。
     *
     * パスワードでもパスキーでも、ここから先は同じ。片方だけ
     * アクセス権の取得を忘れる、といったずれを作らないため。
     */
    applyLoginResponse (data) {
      /*
       * **入口の開閉は、いちばん手前で受け取る（場面 S-48）。**
       *
       * 確認コード待ちの分岐はここで return する。あとに置くと、
       * **確認コード待ちの人にだけ届かない**——いちばん要る人に
       * 届かない、といういちばん悪い形になる。
       *
       * そのつど受け取る。覚えたままにすると、会社や設定が変わった
       * ときに古い判断が残る。
       */
      this.temporaryPassEntranceOpen =
        Boolean(data.temporaryRecoveryPassEnabled)

      /*
       * 確認コードがまだ要るなら、ここで止める。
       *
       * この応答には利用者IDも権限も入っていない。**まだログインして
       * いない**ので、進めるものが無い。パスワードでもパスキーでも
       * 同じところを通るので、片方だけ確認を飛ばすことにならない。
       */
      if (data.mfaRequired) {
        this.mfaRequired = true
        this.mfaCode = ''
        // 入力欄に残ったパスワードは、もう使わない。
        this.password = ''
        return Promise.resolve()
      }

      /*
       * 2要素の登録がまだなら、ここで止める。
       *
       * **確認コードとは別。**あちらは「持っているものを見せて」で、
       * こちらは「まず持ってください」。同じ画面に混ぜると、まだ登録
       * していない人へ「確認コードを入れてください」と出すことになる。
       *
       * この応答にも利用者IDも権限も入っていない。まだログインしていない。
       */

      if (data.mfaEnrollmentRequired) {
        this.mfaCode = ''
        this.password = ''
        /*
         * **押せるかどうかはサーバが決める。**
         *
         * 促しの段なら true、必須の段なら false が返る。画面で
         * 期日を計算して決める形にすると、時計のずれた端末で
         * 押せてしまう。印が無い応答は false（押せない）として扱う。
         *
         * 段の文（いつから必須か・あと何回・復旧か）は、どの入り口でも
         * 同じ口（/mfa/enrollment/pending）から読む（束22）。
         */
        return this.enterEnrollment({
          skippable: Boolean(data.mfaEnrollmentSkippable)
        })
      }

      /*
       * **「近く必須になります」はここで控えない。**
       *
       * 応答には印（upcomingMfaRequirement / mfaRequiredFrom）が載って
       * いるが、ここで控えると **この応答を受け取れる人にしか案内が
       * 出ない**。Google / Microsoft から入った人はこの処理そのものを
       * 通らない（サーバが画面へ飛ばして戻す形なので、応答の中身は
       * 途中で捨てられる）。
       *
       * 同じ会社の同じ人が、入り口を変えただけで案内が出たり出なかったり
       * する。案内の段は「まだ止めていないが、そのうち止まる」を伝える
       * ためだけにあるので、出ない入り口があるなら段を作った意味がない。
       *
       * 移った先の枠（TheContainer）がサーバへ聞き直す。
       */
      sessionStorage.setItem('level', data.level)
      const responseContext = data.accessContext
      const accessReady = responseContext
        ? Promise.resolve(setAccessContext(responseContext))
        : getAccessContext({ force: true })
      return accessReady.then(() => this.$router.replace(this.safeRedirect))
    },

    /**
     * 確認コードを送る。
     *
     * 認証アプリの数字でも、予備のコードでも、同じところへ送る。
     * サーバはどちらだったかを応答で区別しないので、画面も区別しない。
     * 区別して見せると、外から「その人は予備をまだ持っている」を探れる。
     *
     * コードそのものについての理由は出さない。「まだ登録していない」
     * 「コードが違う」「さっき使った」を見分けられると、当たっていた
     * ことまで分かってしまう。サーバもそこは1種類の文言しか返さない。
     *
     * **ただし「もう一度お試しください」が嘘になる場合は分ける。**
     *
     * 2026-08-23 まで、ここは何を断られても同じ固定文を出していた。
     * サーバが返す文言は捨てていた。だから待ち状態が切れていても、
     * 続けて外して止められていても、画面は「もう一度お試しください」と
     * 言い続けた。利用者は正しいコードを入れ直し、また同じ文言を見て、
     * 認証アプリを疑い、しまいには問い合わせる（場面 S-60 / S-61 / S-76）。
     *
     * いまはサーバの文言をそのまま出す。待ち状態が消えたときは入力欄も
     * 閉じて、メールアドレスから聞き直す。**二度と通らない欄を出したまま
     * にしない。**
     */
    async submitMfa () {
      if (this.mfaLoading) return
      if (!this.mfaCode || !this.mfaCode.trim()) return

      this.errors = []
      this.mfaLoading = true
      try {
        const data = await submitMfaCode(this.mfaCode.trim())
        await this.applyLoginResponse(data)
      } catch (error) {
        this.mfaCode = ''
        if (isNetworkFailure(error)) {
          this.errors = [ConstList.NORMAL_ERROR]
        } else {
          const text = serverMessage(error)
            || '確認コードが正しくありません。もう一度お試しください。'
          if (isChallengeGone(error)) {
            // 待ち状態が消えた。入力欄を閉じて、最初から聞き直す。
            this.mfaRequired = false
          }
          this.errors = [text]
        }
      } finally {
        this.mfaLoading = false
      }
    },

    /**
     * 確認コードの入力をやめて、最初から入り直す。
     *
     * <p>サーバ側の途中の状態は放っておいても期限で消える。画面は
     * 入力を捨てて、メールアドレスから聞き直す。</p>
     */
    cancelMfa () {
      this.mfaRequired = false
      /*
       * **登録の段も片づける。**
       *
       * 2026-08-23 まで、ここは確認コードの段しか戻していなかった。
       * 登録パネルにはそもそも押すものが無かったので気づかなかったが、
       * 足すなら両方を戻さないと、メール／パスワードの欄が出ない
       * （どちらの v-if も false でないと出ない）。
       *
       * 共有鍵も捨てる。画面の中だけで持っているものを、戻ったあとも
       * 抱えたままにしない。
       */
      this.enrollmentRequired = false
      this.enrollmentSecret = ''
      this.enrollmentUri = ''
      this.enrollmentQr = ''
      /*
       * 予備のコードも捨てる（断られたあとに「最初からやり直す」を押した
       * とき）。残すと、入り直して登録の段へ送られたときに、前のコードの
       * 画面がそのまま出る。
       */
      this.backupCodes = []
      this.completionRefused = false
      this.mfaCode = ''
      this.errors = []
    },

    /**
     * パスキーでログインする。
     *
     * メールアドレスは聞かない。誰かは鍵からサーバが決める。
     *
     * 断られた理由は画面に出さない。サーバも1種類の文言しか返さない。
     * 「その鍵は知らない」と「期限切れ」を区別して見せると、何が通って
     * 何が通らないかを外から確かめられる。
     */
    /**
     * <b>試したけれど入れなかった入口を覚えておく。</b>
     *
     * <p>ログインをまたぐので sessionStorage。タブを閉じれば消える。
     * **本人が誰かは書かない。**入口の種類だけ。</p>
     */
    rememberTriedEntrance (kind) {
      this.triedButNotRegistered = kind
      try {
        /*
         * **押した時刻も一緒に残す。**
         *
         * 同じタブを次に使う人が、前の人の「先ほどパスキーでは…」を
         * 見ることがある。共有端末では実際に起こる。読む側で5分を
         * 過ぎたものを捨てるために、時刻を添える。
         *
         * **誰が押したかは書かない。**書けば、それこそ残してはいけない
         * ものになる。入口の種類と時刻だけで足りる。
         */
        window.sessionStorage.setItem('preika.triedEntrance',
          JSON.stringify({ kind: kind, at: Date.now() }))
      } catch (error) {
        /*
         * **保存できなくても止めない。**プライベートウィンドウや
         * 保存を切っている端末では例外になる。案内が出ないだけで、
         * ログインそのものは通る。
         */
      }
    },

    async loginWithPasskey () {
      if (this.passkeyLoading || this.loading) return

      clearAccessContext()
      this.errors = []
      this.passkeyHint = ''
      this.passkeyLoading = true
      try {
        const data = await signInWithPasskey()
        await this.applyLoginResponse(data)
      } catch (error) {
        /*
         * **何も出さないのをやめた。**
         *
         * 2026-08-23 まで、ここは何も言わずに return していた。
         * WebAuthn は「利用者がやめた」と「この端末にパスキーが無い」を
         * 区別しないので、**1本も持っていない人が押すと、選択画面が
         * 一瞬出て閉じ、押す前と見た目が同じところへ戻る。**理由も
         * 案内も出ない。押すたびに t_webauthn_challenge が1行増え、
         * 匿名発行の回数も1つ減る（場面 S-64）。
         *
         * 赤い枠には出さない。**やめた人にとっては失敗ではない。**
         * 隣にメールアドレスとパスワードの欄があることだけを、
         * 落ち着いた見た目で伝える。
         */
        /*
         * **取り消しの側でも、押した事実は残す。**
         *
         * WebAuthn の NotAllowedError は「本人がやめた」と「この端末に
         * 使える鍵が無い」を区別しない。区別できない以上、案内が要る
         * ほうへ倒す。**まだ登録していない人がちょうどここを通る。**
         *
         * 2026-09-20 まで、印を残す行は下の失敗側にしか無く、この
         * 早期 return で届いていなかった。登録のダイアログを作ったのに、
         * いちばん出したい相手には出ない状態だった。
         *
         * 印はこのタブ限り（sessionStorage）で、入ったあと1回出るだけ。
         * すでにこの端末へ登録済みの人が誤って押してやめた場合は、
         * ダイアログの中で「すでに登録されています」になる。
         */
        this.rememberTriedEntrance('passkey')

        if (this.isCancelled(error)) {
          /*
           * **入口を名指ししない。**
           *
           * 最初は「メールアドレスとパスワードでログインしてください」と
           * 書いていた。ところが会社は password_login_enabled=false に
           * できる（場面 S-81）。パスワードを閉じてパスキーと Microsoft
           * だけにしている会社では、**閉じている入口を案内する**ことに
           * なる。行き止まりを無くすために直したのに、行き止まりを
           * 指し示していた。
           *
           * 外部IdPで同じ形を一度直している（結び付けに失敗したとき
           * 「パスワードでお試しください」と出していた）。
           *
           * この画面には、その会社で実際に使える入口だけが並んでいる。
           * **そこを見てもらえばいい。**画面は password_login_enabled を
           * 知らないので、名指しできる材料がそもそも無い。
           */
          this.passkeyHint = 'パスキーでのログインは行われませんでした。'
            + ' この端末にパスキーが無い場合は、'
            + 'この画面に出ているほかのログイン方法をお試しください。'
          return
        }
        /*
         * **こちらも入口を名指ししない。**
         *
         * キャンセルの側だけ直して、ここを見落としていた。
         * password_login_enabled=false の会社では、パスキーの認証が
         * サーバ側で失敗したときだけ**閉じている入口を案内する**ことに
         * なっていた。同じ画面の同じボタンなのに、断られ方で片方だけ
         * 嘘をつく。
         */
        /*
         * **直し方も書く。**いちばん多いのは「まだ登録していない」。
         * 2026-08-31、QRを読んでも入れないという問い合わせがあり、
         * 調べたら t_user_passkey_credential に1行も無かった。
         *
         * **入口を名指ししない**のは上のコメントのとおり。
         * 「ほかのログイン方法で入ってから」と条件つきで書けば、
         * 合言葉が閉じている会社にも嘘をつかない。
         */
        this.errors = ['パスキーでログインできませんでした。' +
          'まだ登録していない場合は、この画面に出ているほかの' +
          'ログイン方法でログインしてください。' +
          'ログイン後に、その場で登録できます。']
      } finally {
        this.passkeyLoading = false
      }
    },

    /**
     * 外部IdPへ移る。
     *
     * メールアドレスは聞かない。誰かは戻ってきたトークンからサーバが決める。
     *
     * 行き先はサーバが組み立てて返す。画面で作らないので、戻り先を
     * 差し替えられない。
     *
     * 移ったあとはこの画面を離れるので、読み込み中の表示は戻さない。
     * 戻すと、遷移までの一瞬だけボタンが押せる状態になり、二重に送り出せる。
     */
    async loginWithExternalIdp (idp) {
      if (this.isAnyExternalIdpMoving || this.passkeyLoading || this.loading) return

      clearAccessContext()
      this.errors = []
      this.externalIdpState[idp.key].loading = true
      try {
        const url = await idp.start()
        window.location.assign(url)
      } catch (error) {
        /*
         * 断られた理由は画面に出さない。サーバも1種類の文言しか返さない。
         * 「設定が無い」と「入口が閉じている」を区別して見せると、
         * 外から設定の中身を探れる。
         */
        this.externalIdpState[idp.key].loading = false
        this.errors = [idp.failure]
      }
    },

    /**
     * 外部IdPから戻ってきたときの印を読む。
     *
     * サーバは4つの行き先しか作らない。
     *
     *   ?mfa=1                     … 1つ目は通った。確認コードがまだ要る
     *   ?mfa=enroll                … 1つ目は通った。2要素をまだ持っていない
     *   ?microsoft=failed など     … 通らなかった
     *   （印なし）                  … ここへは来ない（入れたら dashboard へ行く）
     *
     * **印だけで、誰かは入っていない。** 誰かはサーバのセッションが
     * 覚えている。URLに載せると、そこから利用者を推せる。
     *
     * 失敗の理由も入っていない。「結び付いていない」「組織が違う」を
     * 区別して見せると、外から探れる。だからここでも1種類しか言わない。
     */
    applyExternalIdpReturn () {
      /*
       * ルータが無いところでも落ちないこと。
       *
       * mounted は必ず走るので、ここで this.$route を無条件に読むと、
       * ルータを差していない場所でこの画面を組み立てただけで落ちる。
       * 実際、ログインボタンの検査（loginSubmitAlwaysReachable）が
       * それで落ちた。あの検査はルータと関係が無いのに、こちらの都合で
       * 巻き込んだことになる。
       *
       * 印が読めないときは、何もしないのが正しい。読めないことと
       * 「印が無い」ことは、この画面にとって同じ。
       */
      const query = (this.$route && this.$route.query) || {}

      if (String(query.mfa) === '1') {
        /*
         * 確認コードの入力へ切り替える。
         *
         * パスワードのときと同じ状態にするだけで、ここで何も判断しない。
         * 通ったかどうかはサーバが持っている。
         */
        this.mfaRequired = true
        this.mfaCode = ''
        this.password = ''
        /*
         * **一時パスの入口の開閉をサーバに聞く**（束22）。
         *
         * パスワード・パスキーで入った人はログインの応答に載るが、
         * ここへ来た人には届いていない。聞かないと、入口を開けていても
         * ［認証アプリも予備のコードも使えない］が出ず、端末も予備の
         * コードも失くした人がここで行き止まりになる。
         */
        this.loadChallengeState()
      } else if (String(query.mfa) === 'enroll') {
        /*
         * 2要素の登録へ切り替える。
         *
         * **確認コード待ちと別にする。**あちらは「持っているものを
         * 見せて」で、こちらは「まず持ってください」。混ぜると、まだ
         * 登録していない人に確認コードの入力欄が出る。入れられる数字が
         * 無いので、そこから先へ進めない。
         *
         * パスワード・パスキーのときに data.mfaEnrollmentRequired で
         * している処理と同じ。**同じ状態には同じ道を通す。**
         */
        this.mfaCode = ''
        this.password = ''
        /*
         * **段をサーバに聞き直す。**
         *
         * ここへ来た人には、ログインの応答が届いていない（外部IdPは
         * 画面へ飛ばして戻す形なので、途中で捨てられる）。合言葉で
         * 入った人は data.mfaEnrollmentSkippable を受け取っているが、
         * こちらには印しか無い。
         *
         * 聞かないと、外部IdPから入った人だけ「あとで登録する」が
         * 出ない。まだ促しの段なのに、登録するまで先へ進めなくなる。
         * **入り口によって振る舞いが変わるのがいちばん困る。**
         *
         * 聞けなかったときは［あとで］を出さない（出して押させて断られる
         * より、出ないほうがまし。登録の道は塞がっていない）。
         */
        this.enterEnrollment({ skippable: false })
      } else {
        /*
         * 失敗の印は提供元ごとに違う（?microsoft=failed / ?google=failed）。
         * **一覧から探す。** 名前を1つずつ書くと、提供元を足したときに
         * 印だけ増えて読む側が増えず、戻ってきた人に何も出ない。
         */
        const failed = EXTERNAL_IDPS.find(
          idp => String(query[idp.key]) === 'failed')
        if (!failed) return
        /*
         * **パスキーと同じ。**試した入口を覚えておき、入ったあとに
         * 登録する場所へ連れて行く。ここで読んだ案内は、メールと
         * パスワードを入れているうちに忘れられる。
         */
        this.rememberTriedEntrance(failed.key)
        this.errors = [failed.failure]
      }

      /*
       * 印をURLから消す。
       *
       * 残すと、画面を読み込み直すたびに同じ状態へ戻る。とくに失敗の
       * 印が残ると、パスワードで入り直そうとしている人に、消えない
       * 赤い文字を見せ続けることになる。
       */
      if (this.$router) {
        this.$router.replace({ name: 'Login' }).catch(() => {})
      }
    },

    /**
     * 登録を始める。
     *
     * <p>共有鍵は<b>画面の中だけ</b>で持つ。記録にも貼らない。</p>
     *
     * <p>名前は <code>secretBase32</code>。サーバが返しているのは
     * この名前で、<b>ここを間違えても通信は成功する</b>。成功したまま
     * 空文字になり、「認証アプリに次の鍵を登録してください」と言われて
     * 鍵が出ない。実際に <code>secret</code> と読んでいた。</p>
     */
    /**
     * いまの段をサーバから読み直す。
     *
     * <p><b>押せるかどうかを、この画面で決めない。</b>ここで読むのは
     * ボタンを出すかどうかだけ。実際に押されたときは、サーバがもう
     * 一度確かめて断る。画面の状態がそのまま資格になってはいけない。</p>
     *
     * <p>読めなかったときは<b>出さない</b>。出して押させて断られる
     * より、出ないほうがましで、登録の道は塞がっていない。</p>
     */
    /** 一時パスの入力へ切り替える。 */
    showTemporaryPassInput () {
      this.temporaryPassInputShown = true
      this.temporaryPass = ''
      this.errors = []
    },

    /**
     * 確認コードの入力へ戻る。
     *
     * <p><b>打ちかけを残さない。</b>戻ってまた開いたときに前の文字が
     * 残っていると、送るつもりのないものを送ってしまう。</p>
     */
    hideTemporaryPassInput () {
      this.temporaryPassInputShown = false
      this.temporaryPass = ''
      this.errors = []
    },

    /**
     * 一時パスを送る（場面 S-48）。
     *
     * <p>通ると<b>ログインではなく、2要素の登録しかできない状態</b>へ
     * 移る。ふつうのログイン画面へは戻さない——戻すと、一時パスを
     * 使い切ったのに何も進んでいないように見える。</p>
     *
     * <p><b>断り方は1種類。</b>「もう使った」「期限切れ」「違う」を
     * 見分けさせない。サーバも分けていない。</p>
     */
    async submitTemporaryPass () {
      const typed = (this.temporaryPass || '').trim()
      if (!typed) {
        this.errors = ['一時パスを入力してください。']
        return
      }

      this.mfaLoading = true
      this.errors = []
      try {
        const data = await useTemporaryRecoveryPass(typed)

        /* **打ち込んだ文字は残さない。** */
        this.temporaryPass = ''
        this.temporaryPassInputShown = false

        this.mfaRequired = false
        this.mfaCode = ''
        if (data && data.mfaEnrollmentRequired) {
          /*
           * **鍵を取りに行く**（束22）。2026-09-30 まで、ここは段を
           * 立てるだけで鍵を取っていなかった。「次のキーを登録し」とだけ
           * 書いてあり、キーが1文字も出ず、入れられる数字が無かった。
           *
           * 復旧では「あとで」を出さない。
           */
          await this.enterEnrollment({ skippable: false, recovery: true })
        }
      } catch (error) {
        this.errors = [
          serverMessage(error) || '一時パスを確かめられませんでした。'
        ]
      } finally {
        this.mfaLoading = false
      }
    },

    /**
     * 再読込のあと、段をサーバから戻す（場面 S-48）。
     *
     * <h3>これは見た目の話ではない</h3>
     *
     * <p>復旧の途中で F5 されると、この画面の持ち物は全部消える。
     * ふつうの登録なら入り直せば同じ段へ戻れるが、<b>復旧では
     * 戻れない</b>——一時パスはもう使い切っていて、古い2要素も
     * 消えている。入り直しても第2要素を出せず、一時パスも打てない。
     * <b>そこで締め出しになる。</b></p>
     *
     * <p>サーバ側の段は残っているので、ここで聞けば戻せる。</p>
     *
     * <p>読めなかったときは<b>何もしない。</b>ふつうにログイン画面を
     * 出す。まだ誰も居ない状態で開いた人には、これが正しい。</p>
     */
    async restorePendingEnrollment () {
      try {
        const state = await fetchPendingEnrollmentState()
        if (!state || !state.mfaEnrollmentRequired) { return }

        /*
         * **別の入り口が先に段へ入っていたら、何もしない。**
         *
         * Google / Microsoft から ?mfa=enroll で戻ったときは、そちらが
         * 同じ起動の中で鍵を取りに行く。ここでも取りに行くと鍵が2回
         * 作られ、画面に出た鍵とサーバが持つ鍵が食い違って、正しく読み
         * 取った6桁が通らない。
         */
        if (this.enrollmentRequired) return
        this.enrollmentRequired = true
        this.applyPendingState(state, { recovery: false })
      } catch (error) {
        /* 段が無いだけ。ふつうのログイン画面を出す。 */
        return
      }
      /*
       * **鍵を取りに行く**（束22）。2026-09-30 まで、再読込で段を戻した
       * ときは鍵を取っていなかった。案内文だけが出てキーが出ず、復旧の
       * 途中なら入り直しても一時パスはもう無いので、そこで締め出しになった。
       */
      await this.startEnrollment()
    },

    /**
     * 2要素の登録の段へ入る。<b>どの入り口もここを通る</b>（束22）。
     *
     * <p>パスワード・パスキー・Google / Microsoft・一時パス・再読込の
     * どれでも、段の文（いつから必須か・あと何回・復旧か）をサーバに聞き、
     * 鍵と QR を取りに行く。入り口ごとに書くと、片方だけ鍵を取り忘れる
     * （実際に一時パスと再読込でそうなっていた）。</p>
     *
     * @param initial.skippable 段を聞けなかったときの［あとで］（ログインの
     *        応答で届いた値。届いていない入り口は false）
     * @param initial.recovery 一時パスが通った直後か
     */
    async enterEnrollment (initial) {
      const given = initial || {}
      this.enrollmentRequired = true
      this.recoveryEnrollment = Boolean(given.recovery)
      this.enrollmentSkippable = this.recoveryEnrollment
        ? false
        : Boolean(given.skippable)
      await this.loadPendingEnrollmentState(given)
      await this.startEnrollment()
    },

    /**
     * いまの段をサーバから読み直す。
     *
     * <p>読めなかったときは、入り口が知っていた値（ログインの応答）の
     * まま。それも無ければ［あとで］を出さない。</p>
     */
    async loadPendingEnrollmentState (initial) {
      const given = initial || {}
      try {
        const state = await fetchPendingEnrollmentState()
        if (!state) return
        this.applyPendingState(state, given)
      } catch (error) {
        this.enrollmentSkippable = given.recovery
          ? false
          : Boolean(given.skippable)
      }
    },

    /** サーバが返した段を、画面の持ち物へ写す。**押せるかを画面で決めない。** */
    applyPendingState (state, given) {
      this.recoveryEnrollment = Boolean(given && given.recovery) ||
        Boolean(state.temporaryRecoveryEnrollment)
      /* 復旧では「あとで」を出さない。 */
      this.enrollmentSkippable = this.recoveryEnrollment
        ? false
        : Boolean(state.mfaEnrollmentSkippable)
      this.mfaRequiredFrom = state.mfaRequiredFrom || ''
      this.enrollmentSkipsRemaining =
        typeof state.mfaEnrollmentSkipsRemaining === 'number'
          ? state.mfaEnrollmentSkipsRemaining
          : null
    },

    /**
     * 確認コード待ちの人が、一時パスの入口の開閉を聞く（束22）。
     *
     * <p>Google / Microsoft から戻った人のため。読めなければ出さない
     * （閉じているのに出すと、押しても必ず断られるボタンになる）。</p>
     */
    async loadChallengeState () {
      try {
        const state = await fetchMfaChallengeState()
        this.temporaryPassEntranceOpen =
          Boolean(state && state.temporaryRecoveryPassEnabled)
      } catch (error) {
        this.temporaryPassEntranceOpen = false
      }
    },

    async startEnrollment () {
      try {
        const options = await startPendingEnrollment()
        this.enrollmentSecret = (options && options.secretBase32) || ''
        this.enrollmentUri = (options && options.otpauthUri) || ''
        /*
         * QR は端末の中で描く（utils/otpQr.js）。作れなくても、畳んだ
         * 鍵を手で入れる道が残る。
         */
        this.enrollmentQr = await otpauthQrDataUrl(this.enrollmentUri)
      } catch (error) {
        this.errors = [this.explainEnrollment(error,
          '2要素の登録を始められませんでした。')]
      }
    },

    /**
     * 登録を仕上げる。<b>ここではまだ入らない。</b>
     *
     * <p>予備のコードは<b>この1回しか見せられない</b>。控えたことを
     * 確かめてから先へ進ませる。</p>
     *
     * <p>サーバもここではセッションを作らない。応答にログインの中身が
     * 入っていないのはそのため。<b>画面で止めるだけでは足りない</b>ので、
     * 受け取る側も持たない形にしてある。</p>
     */
    async submitEnrollment () {
      if (this.mfaLoading) return

      this.mfaLoading = true
      this.errors = []
      try {
        const result = await confirmPendingEnrollment(this.mfaCode)
        this.completionRefused = false
        this.backupCodes = (result && result.backupCodes) || []
        this.enrollmentSecret = ''
        this.enrollmentUri = ''
        this.enrollmentQr = ''
      } catch (error) {
        this.errors = [this.explainEnrollment(error,
          '確認コードを確認できませんでした。数字をもう一度お確かめください。')]
      } finally {
        this.mfaLoading = false
      }
    },

    /**
     * 予備のコードを控えたので、先へ進む。
     *
     * <p>ここで初めて中へ入る。<b>控える前に進ませない。</b>
     * 進ませると、端末を失くしたときの逃げ道が無いまま運用が始まる。</p>
     *
     * <p>入るのを決めるのはサーバ。ここで頼んで、返ってきた応答で
     * 進む。<b>画面が持っていた値で進めない。</b>持っていた値で進める
     * 形だと、サーバ側は6桁の時点で入れてしまっていることになる。</p>
     *
     * <p>断られたら、予備のコードは消さずに残す。消してから断られると、
     * 控え損ねたまま画面から消える。</p>
     *
     * <p>ここで断られると、控え待ちの状態はもう使い切られている
     * （1回ぶんの資格で2回入れないようにするため）。だから
     * <b>やり直しても通らない</b>。案内は「もう一度」ではなく
     * 「ログインし直して」にする。登録そのものは済んでいるので、
     * 認証アプリの6桁で入れる。いま出ているコードもそのまま使える。</p>
     */
    /**
     * 登録を「あとで」にして、そのまま入る。
     *
     * **押せるかどうかはサーバが決める。**画面が押せると思っていても、
     * そのあいだに施行日が来ていれば断られる。断られたら、そのまま
     * 登録の段に留まる。
     */
    async skipEnrollment () {
      if (this.mfaLoading) return

      this.mfaLoading = true
      this.errors = []
      try {
        const data = await skipPendingEnrollment()
        this.enrollmentRequired = false
        this.enrollmentSecret = ''
        this.enrollmentUri = ''
        this.enrollmentQr = ''
        await this.applyLoginResponse(data)
      } catch (error) {
        /*
         * 断られたら、**登録の段から出さない**。出すと、押した人は
         * ログイン画面へ戻されて何が起きたか分からない。
         */
        this.errors = [this.explainEnrollment(error,
          '「あとで」にできませんでした。'
          + '2要素認証の登録が必要な期間に入っている可能性があります。'
          + 'そのまま登録を進めてください。')]
      } finally {
        this.mfaLoading = false
      }
    },

    async finishEnrollment () {
      if (this.mfaLoading) return

      this.mfaLoading = true
      this.errors = []
      try {
        const data = await completePendingEnrollment()
        this.enrollmentRequired = false
        this.backupCodes = []
        this.completionRefused = false
        await this.applyLoginResponse(data)
      } catch (error) {
        /*
         * サーバが断った（応答が返ってきた）ときだけ、やり直しの道を出す。
         * 通信が届かなかっただけなら、控え待ちは残っているかもしれない
         * ので、もう一度押せるままにする。
         */
        if (!isNetworkFailure(error)) {
          this.completionRefused = true
        }
        this.errors = [this.explainEnrollment(error,
          'ログインを仕上げられませんでした。'
          + 'お手数ですが、もう一度ログインからやり直してください。'
          + '2要素の登録は済んでいます。いま出ている予備のコードも'
          + 'そのままお使いいただけます。')]
      } finally {
        this.mfaLoading = false
      }
    },

    /**
     * 断られた理由の見せ方。
     *
     * <p>通信が届かないことだけを分ける。それ以外は1種類にまとめる。
     * 細かく分けると、応答の違いから外の人が中の状態を推せる。</p>
     */
    explainEnrollment (error, fallback) {
      if (isNetworkFailure(error)) {
        return '通信できませんでした。接続を確認してからお試しください。'
      }
      /*
       * **サーバの文言を捨てない。**
       *
       * 2026-08-23 まで、ここは何を断られても決まり文句だけを出していた。
       * 暗号鍵が設定されていない環境では POST /mfa/enrollment/pending/start
       * が必ず 503 になるのに、サーバが返した「2要素認証の設定がありません。
       * 管理者へお問い合わせください。」は画面へ届かず、
       * 「2要素の登録を始められませんでした。」しか出なかった。
       *
       * **鍵も出ない、理由も出ない、進む道も無い。**何が起きているのか
       * 誰にも分からない（場面 S-62）。
       */
      return serverMessage(error) || fallback
    },

    /** 利用者が生体認証の画面を閉じた、など。失敗ではない。 */
    isCancelled (error) {
      if (!error) return false
      if (error.message === 'PASSKEY_CANCELLED') return true
      return error.name === 'NotAllowedError' || error.name === 'AbortError'
    },

    /**
     * 自動入力された値を画面の状態へ取り込む。
     *
     * パスワードマネージャやブラウザの自動入力は、input欄へ値を書くだけで
     * inputイベントを出さないことがある。その場合 v-model は更新されないので、
     * 利用者にはメールアドレスとパスワードが見えているのに、送信されるのは
     * 空文字になる。「入力しているのに入力してくださいと言われる」状態。
     *
     * 送信の直前にDOM側の値を見て、食い違っていれば取り込む。
     */
    syncAutofilledValues () {
      const email = document.getElementById('preika-login-email')
      const password = document.getElementById('preika-login-password')

      if (email && typeof email.value === 'string' && email.value !== this.email) {
        this.email = email.value
      }
      if (password && typeof password.value === 'string' && password.value !== this.password) {
        this.password = password.value
      }
    },

    async loginData () {
      if (this.loading) return

      this.syncAutofilledValues()

      // ボタンを常に押せるようにした分、入力の確認はここで行う。
      // 自動入力で検証が走っていない場合も、押した時点で確かめられる。
      const observer = this.$refs.obs
      if (observer) {
        // 入力の確認を待つあいだに押し直されても2本目を走らせないよう、先に処理中にする
        this.loading = true
        let valid = false
        try {
          valid = await observer.validate()
        } finally {
          if (!valid) this.loading = false
        }
        if (!valid) return
      }

      clearAccessContext()
      this.errors = []
      this.loading = true

      const params = new URLSearchParams()
      params.append(ConstList.EMAIL, this.email)
      params.append(ConstList.PASSWORD, this.password)

      axios
        .post(process.env.VUE_APP_API_ORIGIN + '/check', params)
        .then(response => {
          if (response.data.result === ConstList.SUCCESS) {
            return this.applyLoginResponse(response.data)
          }

          this.errors.push(ConstList.NORMAL_ERROR)
          this.$router.push({
            name: ConstList.ROUTE_ERROR
          })
        })
        .catch(error => {
          // 認証に失敗しただけで「システム通信エラーです。管理者に問い合わせて
          // ください。」が出ていた。原因は、失敗時に GET /error/frontError/... を
          // 呼んでいたこと。この経路はサーバにPOSTしか無いため必ず404になり、
          // 内側のcatchへ落ちて通信エラーの文言が出る。
          // パスワードを打ち間違えただけの人が管理者へ問い合わせることになる。
          const status = error && error.response ? Number(error.response.status) : 0

          if (status === 401) {
            /*
             * **どの失敗でも、まったく同じ文言を出す**（場面 X-15）。
             *
             * 見分けられるようにすると、この口が
             * **「その合言葉は正しい」と教える判定器**になる。
             * candidate-A → いつもの文言、candidate-B → 別の文言、と
             * 試せば candidate-B がその人の合言葉だと分かってしまう。
             * パスワードの入口を閉じた会社でも、その合言葉はほかの
             * サービスで使い回されている。
             *
             * だから、次の3つを1つの文言にまとめる。
             *
             *   - メールアドレスが登録されていない
             *   - 合言葉が違う
             *   - 合言葉は合っているが、この会社ではパスワードを
             *     使えなくしている（場面 X-15）
             *
             * 3つ目の人は、パスキーに対応していない端末しか持たないと
             * **開いている入口が1つも無い。**その人が読み進められるよう、
             * 文言に「ほかのログイン方法」と「管理者へ」を入れる。
             *
             * **入口は名指ししない。**その会社でパスキーも閉じていれば、
             * 閉じている入口を案内することになる（S-64 と同じ理由）。
             * この画面には、その会社で実際に使える入口だけが並んでいる。
             */
            this.errors = [LOGIN_FAILED_MESSAGE]
            return
          }

          if (!status) {
            this.errors = [ConstList.SYSTEM_COMMUNICATION_ERROR]
            return
          }

          const serverMessage = error.response.data && error.response.data.message
          this.errors = [serverMessage || ConstList.SYSTEM_ERROR]
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.preika-login-product-link {
  display: flex;
  min-height: 42px;
  align-items: center;
  justify-content: space-between;
  margin: 16px 0 22px;
  padding: 10px 13px;
  border: 1px solid rgba(30, 125, 188, .24);
  border-radius: 10px;
  background: rgba(232, 246, 253, .72);
  color: #0a659f;
  font-size: 12px;
  font-weight: 800;
  text-decoration: none;
  transition: border-color .16s ease, background .16s ease, transform .16s ease;
}

.preika-login-product-link:hover,
.preika-login-product-link:focus {
  border-color: rgba(30, 125, 188, .48);
  outline: none;
  background: #eef9fe;
  color: #075787;
  text-decoration: none;
  transform: translateY(-1px);
}

.preika-login-product-link .c-icon {
  width: 14px;
  height: 14px;
}
</style>
